import { globalState } from '../app/global-state';

export interface HistoryChangedData {
	from: any;
	to: any;
	history: any[];
	direction: 'back' | 'forward' | 'reload';
	handled?: boolean;
	stopPropagation?: boolean;
}

export type HistoryChangedListener = (e: HistoryChangedData) => void;

interface HistoryOnSteroids extends History {
	__historyExtendedInitialized: boolean;
	// stateIndex: number
	// states: string[][]
	// silentPop: boolean

	getCurrentIndex: () => void;
	silentBack: () => void;
	clearForward: () => void;
	nextBackWillNavigateAway: () => boolean;
	addHistoryChangedListener: (listener: HistoryChangedListener) => void;
	removeHistoryChangedListener: (listener: HistoryChangedListener) => void;
}

const history: HistoryOnSteroids = globalState.history as any;
if (history != null && !history.__historyExtendedInitialized) {
	history.__historyExtendedInitialized = true;

	(function () {
		const originalPushState = history.pushState;
		const originalReplaceState = history.replaceState;
		const listeners: HistoryChangedListener[] = [];
		const killEvent = (event: PopStateEvent) => {
			event.preventDefault();
			event.stopPropagation();
			event.stopImmediatePropagation();
			return false;
		};

		let currentIndex = 0;
		let previousState = null;
		const stateHistory = []; // Array to store the timeline of states
		let silentMode = false; // Flag to suppress events

		// Function to log state into history
		const logState = (state: any, index: number) => {
			stateHistory[index] = { ...state, __heindex: index };
		};

		// Override pushState
		history.pushState = function (
			state,
			title,
			url,
		) {
			if (state instanceof URL) {
				state = state.toString();
			}

			if (url instanceof URL) {
				url = url.toString();
			}

			previousState = { ...history.state, __heindex: currentIndex };
			currentIndex++;
			const enhancedState = { ...state, __heindex: currentIndex, __url: url };

			logState(enhancedState, currentIndex); // Log new state to history
			originalPushState.call(
				history,
				enhancedState,
				title,
				url,
			);
			// dispatchHistoryChangedEvent("forward", previousState, enhancedState);
		};

		history.getCurrentIndex = function () {
			return currentIndex;
		};

		// Override replaceState
		history.replaceState = function (
			state,
			title,
			url,
		) {
			const enhancedState = { ...state, __heindex: currentIndex, __url: url };
			logState(enhancedState, currentIndex); // Replace state in history
			originalReplaceState.call(
				history,
				enhancedState,
				title,
				url,
			);
		};

		// Adds history changed handler
		history.addHistoryChangedListener = function (listener) {
			listeners.push(listener);
		};

		// Removes history changed handler
		history.removeHistoryChangedListener = function (listener) {
			const indexToRemove = listeners.findIndex(item => item === listener);
			if (indexToRemove !== -1) {
				listeners.splice(indexToRemove, 1);
			}
		};

		// Popstate listener to detect navigation direction
		globalState.addEventListener('popstate', (event) => {
			const previousIndex = currentIndex;
			previousState = stateHistory[previousIndex] || null;

			currentIndex = event.state ? event.state.__heindex || 0 : 0;
			const toState = stateHistory[currentIndex] || event.state || {};

			if (toState?.__forwardBustState?.perform == true) {
				const newState = toState.__forwardBustState.state;
				delete toState.__forwardBustState;
				setTimeout(() => {
					history.pushState(
						newState,
						null,
						null,
					);
				}, 2);

				return killEvent(event);
			}

			if (toState?.__silentMode == true) {
				delete toState.__silentMode;
				return killEvent(event);
			}

			if (silentMode) {
				silentMode = false;
				return killEvent(event);
			}

			let direction: 'back' | 'forward' | 'reload';
			if (currentIndex > previousIndex) {
				direction = 'forward';
			} else if (currentIndex < previousIndex) {
				direction = 'back';
			} else {
				direction = 'reload';
			}

			const eventData: HistoryChangedData = {
				direction,
				from: previousState,
				to: toState,
				history: stateHistory.slice(0, currentIndex + 1),
			};

			for (const item of listeners) {
				try {
					item(eventData);
					if (eventData.stopPropagation == true) {
						return killEvent(event);
					}
				} catch (error) {
					console.error(error);
				}
			}
		});

		// Preserve current state on load and add index
		globalState.addEventListener('load', () => {
			const initialState = history.state || {};
			if (!initialState.__heindex) {
				history.replaceState({ ...initialState, __heindex: currentIndex }, '');
			} else {
				currentIndex = initialState.__heindex;
			}

			logState(history.state, currentIndex);
			previousState = history.state;
		});

		// Add silent back navigation method
		history.silentBack = function () {
			const targetState = stateHistory[currentIndex - 1];
			if (targetState != null) {
				targetState.__silentMode = true;
				history.back();
			} else {
				// Do it the old way by using flag
				silentMode = true;
				history.back();
			}
		};

		// Add clear forward navigation method
		history.clearForward = function () {
			if (currentIndex > 0) {
				const prevState = stateHistory[currentIndex];
				const targetState = stateHistory[currentIndex - 1];

				if (prevState != null && targetState != null) {
					targetState.__forwardBustState = {
						perform: true,
						state: prevState,
					};

					history.back();
				}
			}
		};

		history.nextBackWillNavigateAway = function () {
			return currentIndex < 1;
		};
	})();
}

export default class HistoryExtended {
	static addPopstateListener(handler: HistoryChangedListener) {
		if (!history) {
			return;
		}

		history.addHistoryChangedListener(handler);
	}

	static removePopstateListener(handler: HistoryChangedListener) {
		if (!history) {
			return;
		}

		history.removeHistoryChangedListener(handler);
	}

	static nextBackWillNavigateAway(): boolean {
		if (!history) {
			return;
		}

		return history.nextBackWillNavigateAway();
	}

	static back(args?: { silent?: boolean }): void {
		if (!history) {
			return;
		}

		if (args?.silent == true) {
			history.silentBack();
		} else {
			history.back();
		}
	}

	static forward(): void {
		if (!history) {
			return;
		}

		history.forward();
	}

	static go(delta?: number): void {
		if (!history) {
			return;
		}

		history.go(delta);
	}

	static pushState(
		data: any,
		unused: string,
		url?: string | URL | null,
	): void {
		if (!history) {
			return;
		}

		history.pushState(
			data,
			unused,
			url,
		);
	}

	static replaceState(
		data: any,
		unused: string,
		url?: string | URL | null,
	): void {
		if (!history) {
			return;
		}

		history.replaceState(
			data,
			unused,
			url,
		);
	}

	static clearForward() {
		if (!history) {
			return;
		}

		history.clearForward();
	}
}
